import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_CONTRACTORS } from "../../../utils/constants";
import contractorService from "../../../services/ticketService/contractorService";
import { ContractorInterface } from "../../../models/TicketModule/Contractor";

interface DeleteConctractorContext {
  previousContractors: ContractorInterface[];
  deletedContractor: ContractorInterface | undefined;
}

const useDeleteContractor = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    void, // No return data after deletion
    Error,
    string, // ID of the status to delete
    DeleteConctractorContext
  >({
    mutationFn: contractorService.Remove, // Remove should take a string ID

    onMutate: (contractorId: string) => {
      const previousContractors =
        queryClient.getQueryData<ContractorInterface[]>([
          CACHE_KEY_CONTRACTORS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      // Optimistically update the cache
      queryClient.setQueryData<ContractorInterface[]>(
        [
          CACHE_KEY_CONTRACTORS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        previousContractors.filter(
          (contractor) => contractor.id !== contractorId
        ) // Use contractorId to filter
      );

      return {
        previousContractors,
        deletedContractor: previousContractors.find(
          (contractor) => contractor.id === contractorId
        ),
      };
    },

    onError: (error, contractorId, context) => {
      if (!context) return;

      // Rollback to the previous state in case of error
      queryClient.setQueryData<ContractorInterface[]>(
        [
          CACHE_KEY_CONTRACTORS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousContractors
      );
    },

    onSuccess: () => {
      // Optionally, you could perform additional actions on success
      // For example, invalidating queries or updating other parts of the state
    },
  });
};

export default useDeleteContractor;
